export type DifficultyLevel = 'Beginner' | 'Intermediate' | 'Advanced';
export type TripType = 'day-hike' | 'weekend' | 'week-long';

export interface Trip {
  id: string;
  title: string;
  slug: string;
  description: string;
  shortDescription: string;
  location: string;
  region: string;
  difficulty: DifficultyLevel;
  duration: string;
  durationDays: number;
  price: number;
  tripType: TripType;
  maxCapacity: number;
  imageUrl: string;
  features: string[];
  included: string[];
  notIncluded: string[];
  itinerary: { day: number; title: string; description: string }[];
  physicalRequirements: string;
  bestSeasons: string[];
  featured?: boolean;
}

export interface Testimonial {
  id: string;
  name: string;
  tripTaken: string;
  date: string;
  rating: number;
  quote: string;
  avatarUrl?: string;
}

export interface GalleryImage {
  id: string;
  url: string;
  caption: string;
  location: string;
  date: string;
  alt: string;
}

export const trips: Trip[] = [
  {
    id: '1',
    title: 'Mogollon Rim Wilderness Trek',
    slug: 'mogollon-rim-wilderness-trek',
    description:
      'Explore Arizona\'s stunning Mogollon Rim with dispersed camping in Coconino National Forest. Trek through ponderosa pine forests, camp beside pristine mountain streams, and wake up to panoramic views. No permits required - we practice Leave No Trace principles and camp in established dispersed sites.',
    shortDescription: 'Ponderosa pine forests and mountain streams with no permit hassles',
    location: 'Coconino National Forest, Arizona',
    region: 'Arizona',
    difficulty: 'Intermediate',
    duration: '4 Days, 3 Nights',
    durationDays: 4,
    price: 850,
    tripType: 'weekend',
    maxCapacity: 8,
    imageUrl: '/images/generated/photograph-mogollon-rim-arizona.png',
    features: [
      'Dispersed camping - no permits required',
      'Expert wilderness guide',
      'All camping gear provided',
      'Flexibility to choose best campsites',
    ],
    included: [
      'Professional wilderness guide',
      'All camping equipment (tents, sleeping bags, pads)',
      'Group cooking gear',
      'First aid kit',
      'Water filtration system',
      'Leave No Trace instruction',
    ],
    notIncluded: [
      'Personal hiking gear (boots, backpack, clothing)',
      'Food and snacks',
      'Transportation to/from trailhead',
      'Travel insurance',
    ],
    itinerary: [
      {
        day: 1,
        title: 'Enter the Rim Country',
        description:
          'Meet near Payson. Hike into the Mogollon Rim backcountry through towering ponderosa pines. Set up camp at a scenic dispersed site along East Clear Creek.',
      },
      {
        day: 2,
        title: 'Creek Exploration',
        description:
          'Day hike along the creek, discovering waterfalls and swimming holes. Practice wilderness skills and enjoy the solitude of rough camping.',
      },
      {
        day: 3,
        title: 'Rim Views',
        description:
          'Break camp and trek to a new location near the rim edge. Spectacular views over the Tonto Basin. Evening campfire (when fire restrictions allow).',
      },
      {
        day: 4,
        title: 'Return Journey',
        description:
          'Morning hike along the rim with incredible vistas. Descend back through the forest to the trailhead.',
      },
    ],
    physicalRequirements:
      'Moderate fitness required. Comfortable hiking 8-12 miles per day with a 30-35 lb pack. Some elevation changes but accessible to most.',
    bestSeasons: ['Spring', 'Summer', 'Fall'],
    featured: true,
  },
  {
    id: '2',
    title: 'West Fork Oak Creek Wilderness',
    slug: 'west-fork-oak-creek-wilderness',
    description:
      'Journey deep into one of Sedona\'s most beautiful canyons on this backcountry adventure. Hike beyond where the crowds turn back and experience the dramatic West Fork of Oak Creek Canyon. Wade through crystal-clear creek waters, camp beneath towering red rock walls, and explore a pristine wilderness corridor. Perfect for those seeking Sedona\'s beauty with backcountry solitude.',
    shortDescription: 'Sedona\'s most scenic canyon with creek crossings and red rock camping',
    location: 'West Fork Oak Creek, Sedona, Arizona',
    region: 'Arizona',
    difficulty: 'Intermediate',
    duration: '3 Days, 2 Nights',
    durationDays: 3,
    price: 675,
    tripType: 'weekend',
    maxCapacity: 8,
    imageUrl: '/images/generated/west-fork-oak-creek.png',
    features: [
      'Sedona red rock canyon hiking',
      'Creek crossings and water features',
      'Backcountry camping beyond day hikers',
      'Spectacular slot canyon sections',
    ],
    included: [
      'Professional wilderness guide',
      'All camping equipment',
      'Group cooking gear and stove',
      'Water filtration system',
      'Leave No Trace instruction',
    ],
    notIncluded: [
      'Personal hiking gear and clothing',
      'Personal backpack and boots',
      'Food and snacks',
      'Red Rock Pass ($5/day)',
      'Transportation',
    ],
    itinerary: [
      {
        day: 1,
        title: 'Into West Fork Canyon',
        description:
          'Start at West Fork Trailhead. Hike 3.5 miles on maintained trail, then continue into the backcountry where permits allow camping. Multiple creek crossings lead to our first camp beneath the canyon walls.',
      },
      {
        day: 2,
        title: 'Deep Canyon Exploration',
        description:
          'Full day exploring deeper into the canyon. Wade through narrows, discover hidden alcoves, and practice wilderness skills. Return to camp for evening under the stars.',
      },
      {
        day: 3,
        title: 'Return Journey',
        description:
          'Morning creek exploration and camp breakdown. Hike out through the stunning red rock corridor, arriving at trailhead by afternoon.',
      },
    ],
    physicalRequirements:
      'Moderate fitness required. Comfortable hiking 6-8 miles per day with creek crossings. Must be comfortable getting feet wet. 25-30 lb pack.',
    bestSeasons: ['Spring', 'Summer', 'Fall'],
    featured: true,
  },
  {
    id: '3',
    title: 'White Mountains Backcountry Expedition',
    slug: 'white-mountains-backcountry-expedition',
    description:
      'Embark on an unforgettable Arizona adventure in the White Mountains. Trek through pristine wilderness with dispersed camping in Apache-Sitgreaves National Forest. Navigate alpine meadows, aspen groves, and mountain streams without the hassle of permits. Experience true backcountry freedom in one of Arizona\'s most beautiful regions.',
    shortDescription: 'Arizona alpine wilderness with dispersed camping freedom',
    location: 'Apache-Sitgreaves National Forest, Arizona',
    region: 'Arizona',
    difficulty: 'Advanced',
    duration: '6 Days, 5 Nights',
    durationDays: 6,
    price: 1075,
    tripType: 'week-long',
    maxCapacity: 6,
    imageUrl: '/images/generated/white-mountains-arizona-alpine.png',
    features: [
      'Wilderness expedition',
      'No permits required',
      'Alpine meadows and streams',
      'True backcountry experience',
    ],
    included: [
      'Expert wilderness guide with wilderness first responder certification',
      'All camping equipment',
      'Water filtration systems',
      'Bear hang equipment',
      'Satellite communication device',
      'Group safety equipment',
      'All meals and snacks',
    ],
    notIncluded: [
      'Personal hiking gear and clothing',
      'Transportation to/from trailhead',
      'Travel insurance (recommended)',
    ],
    itinerary: [
      {
        day: 1,
        title: 'Trailhead Orientation',
        description:
          'Meet near Greer, AZ. Wilderness safety orientation and gear check. Begin hiking into the backcountry. Set up first camp in aspen grove.',
      },
      {
        day: 2,
        title: 'Into the High Country',
        description:
          'Trek through alpine meadows and dense forest. Establish base camp near a pristine mountain stream. Wildlife spotting.',
      },
      {
        day: 3,
        title: 'Alpine Ridge Exploration',
        description:
          'Day hike to high ridge with panoramic views. Practice navigation skills. Return to base camp.',
      },
      {
        day: 4,
        title: 'Migrate Camp',
        description:
          'Break camp and trek to new location deeper in the wilderness. Cross meadows and streams. Evening campfire.',
      },
      {
        day: 5,
        title: 'Wilderness Skills Day',
        description:
          'Shorter mileage day focused on advanced wilderness skills. Fishing in mountain streams (license required). Relaxation and photography.',
      },
      {
        day: 6,
        title: 'Return Journey',
        description:
          'Hike out through the beautiful White Mountains wilderness. Trip debrief and celebration at the trailhead.',
      },
    ],
    physicalRequirements:
      'Advanced fitness required. Expect 8-12 miles per day with 35-40 lb pack. Previous backpacking experience recommended. Comfortable with off-trail navigation.',
    bestSeasons: ['Summer', 'Early Fall'],
    featured: true,
  },
  {
    id: '4',
    title: 'Superstition Wilderness Day Hike',
    slug: 'superstition-wilderness-day-hike',
    description:
      'Discover the legendary Superstition Mountains just outside Phoenix. This day hike explores desert canyons, ancient rock formations, and offers stunning views of the Sonoran Desert. Perfect for those seeking Arizona adventure without the overnight commitment.',
    shortDescription: 'Arizona desert day hike with stunning canyon views',
    location: 'Superstition Mountains, Arizona',
    region: 'Arizona',
    difficulty: 'Beginner',
    duration: '1 Day (8 hours)',
    durationDays: 1,
    price: 135,
    tripType: 'day-hike',
    maxCapacity: 12,
    imageUrl: '/images/generated/superstition-mountains-arizona-wilderness.png',
    features: [
      'No overnight gear needed',
      'Desert landscape',
      'Rock formations',
      'Perfect introduction to hiking',
    ],
    included: [
      'Professional guide',
      'Trail permits',
      'First aid kit',
      'Group navigation equipment',
    ],
    notIncluded: [
      'Personal water (bring 3 liters)',
      'Snacks and lunch',
      'Sun protection',
      'Hiking boots',
    ],
    itinerary: [
      {
        day: 1,
        title: 'Superstition Day Trek',
        description:
          '8:00 AM start at Peralta Trailhead. Hike to Fremont Saddle for incredible views of Weaver\'s Needle. Return by 4:00 PM.',
      },
    ],
    physicalRequirements:
      'Beginner-friendly. 5-6 miles with 1,400 feet elevation gain. Any fitness level welcome.',
    bestSeasons: ['Fall', 'Winter', 'Spring'],
    featured: false,
  },
  {
    id: '5',
    title: 'Verde River Canyon Overnighter',
    slug: 'verde-river-canyon-overnighter',
    description:
      'Explore the stunning Verde River Canyon with dispersed camping along the riverside. Trek through desert riparian habitat, discover hidden swimming holes, and camp under the stars beside flowing water. No permits required - we use established dispersed camping areas and practice Leave No Trace principles.',
    shortDescription: 'Desert canyon camping with swimming holes and riverside solitude',
    location: 'Tonto National Forest, Arizona',
    region: 'Arizona',
    difficulty: 'Intermediate',
    duration: '3 Days, 2 Nights',
    durationDays: 3,
    price: 630,
    tripType: 'weekend',
    maxCapacity: 8,
    imageUrl: '/images/generated/verde-river-canyon-arizona.png',
    features: [
      'Desert canyon hiking',
      'Riverside dispersed camping',
      'Swimming holes',
      'No permits required',
    ],
    included: [
      'Professional wilderness guide',
      'All camping equipment',
      'Water filtration system',
      'Group cooking gear',
      'First aid kit',
    ],
    notIncluded: ['Food', 'Personal gear', 'Transportation to trailhead'],
    itinerary: [
      {
        day: 1,
        title: 'Enter the Canyon',
        description:
          'Meet near Camp Verde. Hike 6 miles into the canyon along the Verde River. Set up camp at a scenic dispersed site by the water.',
      },
      {
        day: 2,
        title: 'River Exploration',
        description:
          'Full day exploring the river canyon. Discover swimming holes, unique geology, and riparian wildlife. Evening campfire by the river.',
      },
      {
        day: 3,
        title: 'Return Journey',
        description:
          'Morning swim and camp breakdown. Hike out of the canyon with memories of the desert oasis.',
      },
    ],
    physicalRequirements:
      'Moderate fitness needed. 6-8 miles per day with moderate elevation changes. Some rocky terrain.',
    bestSeasons: ['Spring', 'Fall'],
    featured: false,
  },
  {
    id: '6',
    title: 'Sedona Red Rock Adventure',
    slug: 'sedona-red-rock-adventure',
    description:
      'Experience the stunning red rock formations of Sedona on this day hike. Explore unique geological features, visit ancient cliff dwellings, and enjoy panoramic views of the Verde Valley. This accessible adventure showcases the best of Sedona in a single day.',
    shortDescription: 'Day hike through iconic red rock country',
    location: 'Sedona, Arizona',
    region: 'Arizona',
    difficulty: 'Beginner',
    duration: '1 Day (6 hours)',
    durationDays: 1,
    price: 115,
    tripType: 'day-hike',
    maxCapacity: 15,
    imageUrl: '/images/generated/sedona-arizona-red-rock.png',
    features: [
      'Red rock formations',
      'Ancient ruins',
      'Beginner-friendly',
      'Photography opportunities',
    ],
    included: ['Professional guide', 'Red Rock Pass', 'First aid kit'],
    notIncluded: ['Water', 'Snacks', 'Personal gear'],
    itinerary: [
      {
        day: 1,
        title: 'Red Rock Loop',
        description:
          'Morning hike through Cathedral Rock area. Visit Palatki cliff dwellings. Return by early afternoon.',
      },
    ],
    physicalRequirements: 'Easy to moderate. 4-5 miles with minimal elevation gain.',
    bestSeasons: ['Fall', 'Winter', 'Spring'],
    featured: false,
  },
  {
    id: '7',
    title: 'Horton Creek Backpacking Weekend',
    slug: 'horton-creek-backpacking-weekend',
    description:
      'The perfect beginner backpacking trip near Star Valley, Arizona. Learn essential wilderness skills while exploring the beautiful Horton Creek area. Hike into your chosen base camp, practice water filtration and outdoor cooking, and discover a stunning spring-fed creek. No permits required, making this an ideal introduction to backcountry camping.',
    shortDescription: 'Perfect beginner backpacking trip with skills training',
    location: 'Horton Creek, Star Valley, Arizona',
    region: 'Arizona',
    difficulty: 'Beginner',
    duration: '3-4 Days, 2-3 Nights',
    durationDays: 3,
    price: 575,
    tripType: 'weekend',
    maxCapacity: 10,
    imageUrl: '/images/generated/horton-creek-arizona-pristine.png',
    features: [
      'Perfect for first-time backpackers',
      'Comprehensive skills training',
      'No permits required',
      'Beautiful spring-fed creek',
    ],
    included: [
      'Professional wilderness guide',
      'All camping equipment (tents, sleeping bags, pads)',
      'Water filtration systems',
      'Group cooking gear',
      'Skills training materials',
      'First aid kit',
    ],
    notIncluded: [
      'Personal hiking gear (boots, backpack, clothing)',
      'Food and snacks',
      'Transportation to/from trailhead',
    ],
    itinerary: [
      {
        day: 1,
        title: 'Hike to Base Camp',
        description:
          'Meet at the trailhead near Star Valley. Learn basic backpacking techniques and hike into Horton Creek area. Set up base camp at a location chosen by the group based on terrain and preferences.',
      },
      {
        day: 2,
        title: 'Skills Training & Creek Exploration',
        description:
          'Full day of wilderness skills instruction including water filtration, outdoor cooking, Leave No Trace principles, and camp maintenance. Day hike to the beautiful spring that feeds Horton Creek. Practice filtering water and preparing meals as a group.',
      },
      {
        day: 3,
        title: 'Optional Extended Stay',
        description:
          'For 4-day trips: Additional practice day with skills refinement and exploration. For 3-day trips: Break camp and hike out, applying the skills learned.',
      },
      {
        day: 4,
        title: 'Hike Out',
        description:
          'For 4-day trips only: Final morning in camp, then pack up and hike out. Debrief and celebrate your first backpacking adventure!',
      },
    ],
    physicalRequirements:
      'Beginner-friendly. Comfortable hiking 3-5 miles per day with a 25-30 lb pack. Perfect for those new to backpacking. Any fitness level welcome.',
    bestSeasons: ['Spring', 'Summer', 'Fall'],
    featured: true,
  },
  {
    id: '8',
    title: 'Fossil Creek Wilderness Escape',
    slug: 'fossil-creek-wilderness-escape',
    description:
      'Experience the stunning turquoise waters of Fossil Creek, one of Arizona\'s hidden gems. This weekend backpacking trip takes you to a designated Wild and Scenic River with crystal-clear swimming holes, travertine formations, and lush riparian forest. Limited permits make this a truly exclusive wilderness experience.',
    shortDescription: 'Turquoise swimming holes in a pristine Wild and Scenic River',
    location: 'Tonto National Forest, Arizona',
    region: 'Arizona',
    difficulty: 'Intermediate',
    duration: '2 Days, 1 Night',
    durationDays: 2,
    price: 475,
    tripType: 'weekend',
    maxCapacity: 6,
    imageUrl: '/images/generated/fossil-creek-arizona-stunning.png',
    features: [
      'Wild and Scenic River',
      'Turquoise swimming holes',
      'Travertine waterfalls',
      'Permits included',
    ],
    included: [
      'Professional guide',
      'Fossil Creek permit',
      'All camping equipment',
      'Water filtration',
      'First aid kit',
    ],
    notIncluded: [
      'Food and snacks',
      'Personal gear',
      'Transportation to trailhead',
    ],
    itinerary: [
      {
        day: 1,
        title: 'Descent to Paradise',
        description:
          'Meet at Bob Bear Trailhead. Hike 4 miles down into Fossil Creek Canyon. Set up camp near the crystal-clear creek. Afternoon swimming and exploration of travertine pools.',
      },
      {
        day: 2,
        title: 'Creek Exploration & Exit',
        description:
          'Morning swim and waterfall exploration. Photograph the stunning turquoise water. Pack up and hike out (mostly uphill) by early afternoon.',
      },
    ],
    physicalRequirements:
      'Moderate fitness required. 8.5 miles total with 1,500 ft elevation gain on exit. Must be comfortable with stream crossings.',
    bestSeasons: ['Spring', 'Summer', 'Fall'],
    featured: false,
  },
  {
    id: '9',
    title: 'Paria Canyon Expedition',
    slug: 'paria-canyon-expedition',
    description:
      'Embark on the ultimate Arizona slot canyon adventure through Paria Canyon, straddling the Arizona-Utah border. Trek 38 miles through the world\'s longest slot canyon system, with towering walls, narrows sections, and the famous Buckskin Gulch. This is a bucket-list expedition for experienced backpackers seeking one of the Southwest\'s most spectacular wilderness experiences.',
    shortDescription: 'Epic 38-mile journey through world\'s longest slot canyon',
    location: 'Paria Canyon-Vermilion Cliffs Wilderness, Arizona/Utah',
    region: 'Arizona',
    difficulty: 'Advanced',
    duration: '5 Days, 4 Nights',
    durationDays: 5,
    price: 1250,
    tripType: 'week-long',
    maxCapacity: 8,
    imageUrl: '/images/generated/paria-canyon-arizona-slot.png',
    features: [
      'World\'s longest slot canyon',
      'Point-to-point expedition',
      'Buckskin Gulch narrows',
      'Limited BLM permits included',
    ],
    included: [
      'Expert canyon guide with wilderness first responder certification',
      'All camping equipment rated for desert conditions',
      'BLM permits (limited availability)',
      'Satellite communication device',
      'Water filtration and storage systems',
      'Group safety equipment',
      'All meals optimized for desert backpacking',
    ],
    notIncluded: [
      'Personal hiking gear and clothing',
      'Transportation to Wire Pass Trailhead and from Lee\'s Ferry',
      'Travel insurance (highly recommended)',
    ],
    itinerary: [
      {
        day: 1,
        title: 'Wire Pass to Buckskin Gulch',
        description:
          'Meet at Wire Pass Trailhead. Gear check and canyon safety briefing. Enter Buckskin Gulch and navigate through spectacular narrows. Camp at confluence with Paria Canyon.',
      },
      {
        day: 2,
        title: 'Upper Paria Canyon',
        description:
          'Trek through upper canyon sections with towering walls. Multiple stream crossings. Camp at scenic location in mid-canyon.',
      },
      {
        day: 3,
        title: 'Middle Canyon Narrows',
        description:
          'Navigate through dramatic narrows sections. Discover unique geology and canyon features. Camp beneath 800-foot walls.',
      },
      {
        day: 4,
        title: 'Lower Canyon Journey',
        description:
          'Continue through lower Paria Canyon as walls gradually open. More stream crossings and stunning scenery. Final backcountry camp.',
      },
      {
        day: 5,
        title: 'Exit to Lee\'s Ferry',
        description:
          'Final miles through opening canyon. Arrive at Lee\'s Ferry on the Colorado River. Trip celebration and shuttle pickup.',
      },
    ],
    physicalRequirements:
      'Advanced fitness required. 38 miles over 5 days with full pack. Must be comfortable with multiple daily stream crossings, potential deep wading, and challenging terrain. Previous backpacking experience essential.',
    bestSeasons: ['Spring', 'Fall'],
    featured: true,
  },
  {
    id: '10',
    title: 'Camelback Mountain Sunrise Hike',
    slug: 'camelback-mountain-sunrise-hike',
    description:
      'Start your day with Phoenix\'s most iconic hike! Summit Camelback Mountain via Echo Canyon Trail as the sun rises over the Valley. This challenging but short hike rewards early risers with stunning city views, desert landscapes, and a serious workout. Perfect for visitors or locals wanting Arizona\'s best urban wilderness experience.',
    shortDescription: 'Phoenix\'s premier sunrise hike with 360° city views',
    location: 'Phoenix, Arizona',
    region: 'Arizona',
    difficulty: 'Intermediate',
    duration: '1 Day (4 hours)',
    durationDays: 1,
    price: 95,
    tripType: 'day-hike',
    maxCapacity: 15,
    imageUrl: '/images/generated/camelback-mountain-phoenix-arizona.png',
    features: [
      'Sunrise summit experience',
      'Phoenix city views',
      'Excellent workout',
      'Close to downtown',
    ],
    included: [
      'Professional guide',
      'Headlamp (if needed)',
      'First aid kit',
      'Sunrise photography tips',
    ],
    notIncluded: [
      'Personal water (bring 2 liters)',
      'Snacks',
      'Hiking shoes',
    ],
    itinerary: [
      {
        day: 1,
        title: 'Sunrise Summit Push',
        description:
          'Pre-dawn start at Echo Canyon Trailhead. Headlamp hike to summit (1.2 miles, 1,300 ft gain). Watch sunrise from 2,704 ft peak. Descend by mid-morning.',
      },
    ],
    physicalRequirements:
      'Intermediate fitness required. Steep scrambling sections with handrails. 2.4 miles round trip with significant elevation gain.',
    bestSeasons: ['Fall', 'Winter', 'Spring'],
    featured: false,
  },
  {
    id: '11',
    title: 'Sycamore Canyon Rim Trek',
    slug: 'sycamore-canyon-rim-trek',
    description:
      'Explore Arizona\'s second-largest canyon on this spectacular rim-to-rim backpacking adventure. Sycamore Canyon Wilderness offers incredible red rock scenery, ponderosa pine forests, and true solitude - often called the "other Grand Canyon" but without the crowds. This challenging trek takes you from rim to canyon floor and back, showcasing diverse Arizona ecosystems.',
    shortDescription: 'Arizona\'s hidden canyon gem with red rocks and pine forests',
    location: 'Sycamore Canyon Wilderness, Arizona',
    region: 'Arizona',
    difficulty: 'Advanced',
    duration: '4 Days, 3 Nights',
    durationDays: 4,
    price: 895,
    tripType: 'weekend',
    maxCapacity: 8,
    imageUrl: '/images/generated/sycamore-canyon-arizona-wilderness.png',
    features: [
      'Second-largest canyon in Arizona',
      'Rim-to-rim crossing',
      'Pristine wilderness',
      'No permits required',
    ],
    included: [
      'Expert wilderness guide',
      'All camping equipment',
      'Water filtration systems',
      'Group cooking gear',
      'First aid kit',
      'Leave No Trace instruction',
    ],
    notIncluded: [
      'Food and snacks',
      'Personal hiking gear',
      'Transportation to/from trailheads',
    ],
    itinerary: [
      {
        day: 1,
        title: 'Rim Descent',
        description:
          'Start at Kelley Point. Hike down into Sycamore Canyon through ponderosa forest. Steep descent to canyon floor. Camp near seasonal creek.',
      },
      {
        day: 2,
        title: 'Canyon Floor Exploration',
        description:
          'Trek along canyon floor through diverse terrain. Red rock walls tower above. Discover hidden side canyons. Camp mid-canyon.',
      },
      {
        day: 3,
        title: 'Downstream Trek',
        description:
          'Continue through canyon with stunning geology. Multiple creek crossings (seasonal). Camp near exit trail.',
      },
      {
        day: 4,
        title: 'Exit Climb',
        description:
          'Challenging ascent out of canyon. Gain 2,000 feet over 3 miles. Emerge at Parsons Spring Trailhead. Shuttle to starting point.',
      },
    ],
    physicalRequirements:
      'Advanced fitness required. 30+ miles total with significant elevation changes. Must carry 35-40 lb pack. Previous backpacking experience essential.',
    bestSeasons: ['Spring', 'Fall'],
    featured: false,
  },
  {
    id: '12',
    title: 'Chiricahua Mountains Day Hike',
    slug: 'chiricahua-mountains-day-hike',
    description:
      'Discover the "Land of Standing Rocks" in southeastern Arizona. Chiricahua National Monument features bizarre rock pinnacles, balanced rocks, and incredible hike-through formations. This day hike explores the Heart of Rocks loop, showcasing some of the most unique geology in the Southwest. Perfect for photographers and geology enthusiasts.',
    shortDescription: 'Bizarre rock formations in Arizona\'s sky island wilderness',
    location: 'Chiricahua National Monument, Arizona',
    region: 'Arizona',
    difficulty: 'Intermediate',
    duration: '1 Day (7 hours)',
    durationDays: 1,
    price: 145,
    tripType: 'day-hike',
    maxCapacity: 10,
    imageUrl: '/images/generated/chiricahua-mountains-arizona-massive.png',
    features: [
      'Unique rock formations',
      'Heart of Rocks loop',
      'Sky island ecosystem',
      'Photography paradise',
    ],
    included: [
      'Professional guide',
      'National Monument entrance fee',
      'First aid kit',
      'Geology interpretation',
    ],
    notIncluded: [
      'Personal water (bring 3 liters)',
      'Lunch and snacks',
      'Personal gear',
    ],
    itinerary: [
      {
        day: 1,
        title: 'Heart of Rocks Adventure',
        description:
          'Start at Echo Canyon Trailhead. Hike 9.5-mile loop through incredible rock formations including Big Balanced Rock, Punch and Judy, and Thor\'s Hammer. Return by late afternoon.',
      },
    ],
    physicalRequirements:
      'Intermediate fitness. 9.5 miles with 1,800 ft elevation gain. Rocky terrain requires good balance.',
    bestSeasons: ['Spring', 'Fall', 'Winter'],
    featured: false,
  },
];

export const testimonials: Testimonial[] = [
  {
    id: '1',
    name: 'Jessica Chen',
    tripTaken: 'Mogollon Rim Wilderness Trek',
    date: 'May 2024',
    rating: 5,
    quote:
      "Shane's knowledge of the backcountry is unmatched. The Mogollon Rim experience he crafted was more than just a hike - it was a journey into true wilderness. His ability to find the perfect dispersed campsites and share stories about the area's ecology made every step unforgettable.",
    avatarUrl: '/images/personal/shane-portrait.jpg',
  },
  {
    id: '2',
    name: 'Michael Roberts',
    tripTaken: 'West Fork Oak Creek Wilderness',
    date: 'August 2024',
    rating: 5,
    quote:
      "Perfect introduction to backpacking in Sedona! Shane made our first overnight trip comfortable and safe. His patient teaching style and attention to detail helped us feel confident. Hiking through the stunning red rock canyon with creek crossings was absolutely magical.",
    avatarUrl: '/images/personal/shane-portrait.jpg',
  },
  {
    id: '3',
    name: 'Sarah Martinez',
    tripTaken: 'Verde River Canyon Overnighter',
    date: 'March 2024',
    rating: 5,
    quote:
      'Shane made our Verde River trip absolutely incredible. His preparation and knowledge of the area ensured we had the perfect experience. The hidden swimming holes exceeded every expectation, and his photography tips helped us capture memories that will last forever.',
  },
  {
    id: '4',
    name: 'David Thompson',
    tripTaken: 'White Mountains Backcountry Expedition',
    date: 'April 2024',
    rating: 5,
    quote:
      "Incredible journey through the White Mountains. Shane's meticulous planning and navigation skills made this challenging trek achievable. His stories about Arizona's alpine wilderness and Leave No Trace principles brought a deeper appreciation for the land. Truly a once-in-a-lifetime experience.",
  },
  {
    id: '5',
    name: 'Emily Foster',
    tripTaken: 'Superstition Wilderness Day Hike',
    date: 'November 2024',
    rating: 5,
    quote:
      "Great day hike for beginners! Shane's enthusiasm for the desert landscape is contagious. Learned so much about the local flora and fauna. Already planning our next adventure with him.",
  },
  {
    id: '6',
    name: 'Carlos Rivera',
    tripTaken: 'Paria Canyon Expedition',
    date: 'October 2024',
    rating: 5,
    quote:
      "The Paria Canyon expedition was the adventure of a lifetime! Shane's expertise in slot canyon navigation and desert camping made this challenging journey feel safe and achievable. The narrows sections were absolutely breathtaking. Worth every step of those 38 miles!",
  },
  {
    id: '7',
    name: 'Jennifer Lee',
    tripTaken: 'Fossil Creek Wilderness Escape',
    date: 'June 2024',
    rating: 5,
    quote:
      "Those turquoise waters were unreal! Shane secured one of the limited permits and guided us to the most beautiful swimming holes I've ever seen. The perfect weekend escape into Arizona's hidden paradise.",
  },
];

export const galleryImages: GalleryImage[] = [
  {
    id: '1',
    url: '/images/personal/gallery-1.jpg', // Replace with your photo
    caption: 'Golden hour on the mountain trail',
    location: 'Superstition Mountains, Arizona',
    date: 'November 2024',
    alt: 'Mountain trail at sunset with wildflowers',
  },
  {
    id: '2',
    url: '/images/personal/gallery-2.jpg', // Replace with your photo
    caption: 'Shane Stewart - Your wilderness guide',
    location: 'Colorado Rockies',
    date: 'August 2024',
    alt: 'Shane Stewart outdoor portrait',
  },
  {
    id: '3',
    url: '/images/personal/gallery-3.jpg', // Replace with your photo
    caption: 'Sunset over the Grand Canyon',
    location: 'Grand Canyon National Park, Arizona',
    date: 'May 2024',
    alt: 'Hiker silhouette at Grand Canyon sunset',
  },
  {
    id: '4',
    url: '/images/personal/gallery-4.jpg', // Replace with your photo
    caption: 'Ancient rainforest magic',
    location: 'Olympic National Park, Washington',
    date: 'July 2024',
    alt: 'Moss-covered trees in Pacific Northwest rainforest',
  },
  {
    id: '5',
    url: '/images/personal/gallery-5.jpg', // Replace with your photo
    caption: 'Dawn breaks over the canyon rim',
    location: 'Grand Canyon National Park, Arizona',
    date: 'April 2024',
    alt: 'Sunrise over Grand Canyon with layered rock formations',
  },
];
